import { SEOHead } from "@/components/SEOHead";
import { Layout } from "@/components/layout/Layout";
import { Link } from "react-router-dom";
import { FileText, Zap, Shield, CheckCircle, ArrowRight, Clock, Phone, Mail, MessageSquare, Calendar, AlertTriangle, BarChart3, Users, Bot } from "lucide-react";
import { Button } from "@/components/ui/button";

const CRMDataEntryAutomation = () => {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "NoQ5 CRM Data Entry Automation",
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web",
      "description": "Eliminate 100% of manual CRM data entry with AI-powered automation. Automatically log calls, emails, meetings, and update deal fields in Salesforce, HubSpot & Pipedrive.",
      "url": "https://noq5.ai/crm-data-entry-automation",
      "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
      "publisher": { "@type": "Organization", "name": "NoQ5", "url": "https://noq5.ai" }
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        { "@type": "Question", "name": "How can I automate CRM data entry?", "acceptedAnswer": { "@type": "Answer", "text": "Use a CRM AI agent like NoQ5 that automatically captures activities from calls, emails, meetings, and messages, then logs them to the correct contacts, companies, and deals in your CRM — eliminating 100% of manual data entry." } },
        { "@type": "Question", "name": "How much time do sales reps spend on CRM data entry?", "acceptedAnswer": { "@type": "Answer", "text": "Research shows sales reps spend an average of 5.5 hours per week on CRM data entry — that's 28% of their working time spent typing instead of selling. Over a year, that's 275+ hours per rep lost to administrative work." } },
        { "@type": "Question", "name": "Can AI replace manual CRM data entry completely?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. Modern CRM AI agents like NoQ5 can capture and log 100% of sales activities automatically — calls, emails, meetings, Slack messages, and LinkedIn interactions — with 95%+ accuracy, eliminating the need for manual data entry entirely." } }
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://noq5.ai" },
        { "@type": "ListItem", "position": 2, "name": "CRM Agent", "item": "https://noq5.ai/crm-agent" },
        { "@type": "ListItem", "position": 3, "name": "CRM Data Entry Automation", "item": "https://noq5.ai/crm-data-entry-automation" }
      ]
    }
  ];

  const activityTypes = [
    { icon: Phone, title: "Phone Calls", desc: "Every outbound and inbound call logged with duration, outcome, key topics, next steps, and full transcript — mapped to the right contact and deal." },
    { icon: Mail, title: "Emails", desc: "Gmail and Outlook emails automatically associated with CRM contacts. Thread summaries, response times, and engagement signals tracked." },
    { icon: Calendar, title: "Meetings", desc: "Zoom, Google Meet, and Teams meetings logged with attendees, duration, AI-generated summary, action items, and deal-stage recommendations." },
    { icon: MessageSquare, title: "Slack Messages", desc: "Sales-relevant Slack conversations captured and linked to deals. Internal channel discussions about accounts are surfaced as deal context." },
    { icon: Users, title: "LinkedIn Activities", desc: "LinkedIn connection requests, messages, and profile visits tracked and associated with CRM contacts for complete outreach history." },
    { icon: FileText, title: "Document Sharing", desc: "Proposals, contracts, and collateral shared with prospects tracked with open rates, time spent, and page-level engagement." },
  ];

  const timeWasted = [
    { activity: "Logging call notes after every call", time: "45 min/day", yearly: "195 hours/year" },
    { activity: "Updating deal stages and fields", time: "30 min/day", yearly: "130 hours/year" },
    { activity: "Creating follow-up tasks", time: "20 min/day", yearly: "87 hours/year" },
    { activity: "Entering meeting notes and attendees", time: "25 min/day", yearly: "109 hours/year" },
    { activity: "Searching for contact info to add", time: "15 min/day", yearly: "65 hours/year" },
  ];

  return (
    <Layout>
      <SEOHead
        title="CRM Data Entry Automation — Eliminate Manual CRM Updates | NoQ5"
        description="Automate 100% of CRM data entry with AI. NoQ5 automatically logs calls, emails, meetings & updates deal fields in Salesforce, HubSpot & Pipedrive. Save reps 5+ hours/week."
        keywords="CRM data entry automation, automate CRM data entry, CRM data entry AI, eliminate CRM data entry, sales data entry automation, CRM auto-logging, automatic CRM updates, CRM activity logging, sales activity capture, CRM data entry solution, AI CRM data entry, CRM data entry software, stop manual CRM entry, automate salesforce data entry, automate hubspot data entry, CRM admin automation, sales rep time savings"
        canonicalUrl="https://noq5.ai/crm-data-entry-automation"
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-primary/5" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <nav className="text-sm text-muted-foreground mb-6">
              <Link to="/" className="hover:text-primary">Home</Link> / <Link to="/crm-agent" className="hover:text-primary">CRM Agent</Link> / <span className="text-foreground">CRM Data Entry Automation</span>
            </nav>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
              <FileText className="h-4 w-4" />
              <span>CRM Data Entry Automation</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6 leading-tight">
              Eliminate 100% of Manual <span className="text-primary">CRM Data Entry</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Sales reps waste 5.5 hours every week typing into CRM fields. NoQ5's AI agent automatically captures and logs every call, email, meeting, and message — so your team never touches a CRM form again.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/trial">
                <Button size="lg" className="w-full sm:w-auto text-lg px-8 py-6 bg-primary hover:bg-primary/90 text-primary-foreground">
                  Automate CRM Data Entry <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/crm-agent">
                <Button size="lg" variant="outline" className="w-full sm:w-auto text-lg px-8 py-6 border-border text-foreground hover:bg-accent">
                  See Full CRM Agent
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* The Cost of Manual CRM Data Entry */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 text-center">
            The Hidden Cost of Manual CRM Data Entry
          </h2>
          <p className="text-center text-muted-foreground mb-12">Here's where your sales reps' time actually goes — and what it's costing your pipeline.</p>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse bg-card rounded-xl overflow-hidden border border-border">
              <thead>
                <tr className="bg-muted">
                  <th className="text-left p-4 font-semibold text-foreground">Manual CRM Activity</th>
                  <th className="p-4 font-semibold text-foreground text-center">Time Wasted/Day</th>
                  <th className="p-4 font-semibold text-foreground text-center">Annual Cost Per Rep</th>
                </tr>
              </thead>
              <tbody>
                {timeWasted.map((item) => (
                  <tr key={item.activity} className="border-t border-border">
                    <td className="p-4 text-foreground">{item.activity}</td>
                    <td className="p-4 text-center text-destructive font-semibold">{item.time}</td>
                    <td className="p-4 text-center text-destructive font-semibold">{item.yearly}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-primary bg-primary/5">
                  <td className="p-4 text-foreground font-bold">Total Time Lost to CRM Data Entry</td>
                  <td className="p-4 text-center text-destructive font-bold">2h 15min/day</td>
                  <td className="p-4 text-center text-destructive font-bold">586 hours/year</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-center text-sm text-muted-foreground mt-6">
            At an average AE compensation of $150K/year, that's <strong>$42,000 per rep per year</strong> spent on data entry instead of selling.
          </p>
        </div>
      </section>

      {/* What is CRM Data Entry Automation */}
      <section className="py-16 md:py-24 bg-muted/20">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-8 text-center">
            What Is CRM Data Entry Automation?
          </h2>
          <div className="prose prose-lg max-w-none text-muted-foreground space-y-6">
            <p>
              <strong>CRM data entry automation</strong> uses AI to eliminate the manual process of logging sales activities into your CRM. Instead of reps typing call notes, updating deal fields, and creating follow-up tasks after every interaction, a <Link to="/crm-agent" className="text-primary hover:underline">CRM AI agent</Link> captures everything automatically from your existing communication tools.
            </p>
            <p>
              This isn't simple if-then automation or Zapier workflows. A true CRM data entry automation system uses natural language understanding to <em>comprehend</em> what happened in a conversation, extract the right data points, and map them to the correct CRM fields — including context that only a human would typically catch (buying signals, objections, competitive mentions, stakeholder identification).
            </p>

            <h3 className="text-2xl font-bold text-foreground">Why Sales Reps Hate CRM Data Entry</h3>
            <p>
              The core problem isn't the CRM itself — it's the data entry. According to Salesforce's own research, sales reps spend only 28% of their time selling. The rest is consumed by administrative tasks, with CRM data entry being the single largest time drain. This creates a vicious cycle:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Reps skip CRM updates because they're time-consuming → pipeline data becomes incomplete</li>
              <li>Managers can't trust the pipeline → they ask for more manual reporting</li>
              <li>More reporting requirements → reps spend even less time selling</li>
              <li>Less selling time → missed quotas → higher turnover → lost institutional knowledge</li>
            </ul>
            <p>
              CRM data entry automation breaks this cycle entirely. When every activity is captured automatically, data quality improves, managers get real-time visibility, and reps can focus on what they were hired to do: sell.
            </p>
          </div>
        </div>
      </section>

      {/* Activity Types Captured */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Every Sales Activity — Captured and Logged Automatically
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              NoQ5's CRM data entry automation captures activities from every channel your reps use — and logs them to the right CRM record instantly.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {activityTypes.map((type) => (
              <div key={type.title} className="bg-card border border-border rounded-xl p-6 hover:shadow-lg transition-shadow">
                <type.icon className="h-10 w-10 text-primary mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">{type.title}</h3>
                <p className="text-sm text-muted-foreground">{type.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-primary/5">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Give Your Reps 5 Hours Back Every Week
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Eliminate CRM data entry forever. NoQ5 captures every activity, updates every field, and lets your team focus on selling.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/trial">
              <Button size="lg" className="text-lg px-8 py-6 bg-primary hover:bg-primary/90 text-primary-foreground">
                Start Free Trial <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/crm-agent">
              <Button size="lg" variant="outline" className="text-lg px-8 py-6 border-border text-foreground hover:bg-accent">
                See Full CRM Agent
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Internal Links */}
      <section className="py-12 border-t border-border">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-xl font-semibold text-foreground mb-6">Related Resources</h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            <Link to="/crm-agent" className="text-primary hover:underline text-sm">CRM AI Agent (Pillar Page)</Link>
            <Link to="/crm-enrichment-agent" className="text-primary hover:underline text-sm">CRM Enrichment Agent</Link>
            <Link to="/ai-agent-salesforce" className="text-primary hover:underline text-sm">AI Agent for Salesforce</Link>
            <Link to="/blog/why-sales-reps-hate-crms" className="text-primary hover:underline text-sm">Why Sales Reps Hate CRMs</Link>
            <Link to="/blog/crm-automation-guide" className="text-primary hover:underline text-sm">CRM Automation Guide</Link>
            <Link to="/ai-workflow-automation" className="text-primary hover:underline text-sm">AI Workflow Automation</Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default CRMDataEntryAutomation;
